import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import type { SettingFormMode } from './settingFormMode.types'

type SettingEditorBannersProps = {
  lacksTimeSeriesAccess: boolean
  isEventSetting: boolean
  mode: SettingFormMode
  copyActionLabel: string
  isBaseSettingInCollectedSettings: boolean
}

export function SettingEditorBanners({
  lacksTimeSeriesAccess,
  isEventSetting,
  mode,
  copyActionLabel,
  isBaseSettingInCollectedSettings,
}: SettingEditorBannersProps): ReactNode {
  const { t } = useTranslation()

  return (
    <>
      {lacksTimeSeriesAccess && (
        <InfoBanner text={t('Unfortunately, you do not have permission to make changes here')} style="warning" />
      )}
      {mode === 'copy' ? (
        <CopyBanner isEventSetting={isEventSetting} actionLabel={copyActionLabel} />
      ) : (
        isEventSetting && (
          <InfoBanner
            data-testid="event-linked-readonly-banner" // e2e: read-only event-linked banner — do not remove
            text={t(
              'This setting is connected to an event and cannot be modified here directly. Open up the event to be able to edit it.'
            )}
            style="warning"
          />
        )
      )}
      {isBaseSettingInCollectedSettings && (
        <InfoBanner text={t('Base settings can only be viewed in collected settings, not modified.')} style="info" />
      )}
    </>
  )
}

type CopyBannerProps = {
  isEventSetting: boolean
  actionLabel: string
}

function CopyBanner({ isEventSetting, actionLabel }: CopyBannerProps): ReactNode {
  const { t } = useTranslation()

  if (isEventSetting) {
    return (
      <InfoBanner
        data-testid="event-linked-readonly-banner" // e2e: event-linked banner — do not remove
        text={t(
          'You are copying a setting. The copy is not created until you click "{{action}}", and it is saved as an independent setting, not linked to any event.',
          { action: actionLabel }
        )}
        style="warning"
      />
    )
  }

  return (
    <InfoBanner
      text={t('You are copying a setting. The copy is not created until you click "{{action}}".', {
        action: actionLabel,
      })}
      style="info"
    />
  )
}
